import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/header";
import Footer from "../components/footer";
import Loader from "../components/loader";
import { blogService } from "../../services/blogService";
import { useCategorySEO } from "../../hooks/useSEO";

const BlogCategory = () => {
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [blogs, setBlogs] = useState([]);
  const [category, setCategory] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { categorySlug } = useParams();
  const navigate = useNavigate();

  const postsPerPage = 9;

  // Use SEO hook for category page
  useCategorySEO(category, blogs);

  useEffect(() => {
    loadCategoryData();
  }, [categorySlug]);

  useEffect(() => {
    if (category) {
      loadBlogs();
    }
  }, [currentPage, category]);

  const loadCategoryData = async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);
      
      // Load category info and blogs
      const blogsData = await blogService.getBlogsByCategory(categorySlug, {
        page: 1,
        limit: postsPerPage
      });
      
      // API returns "posts" array, not "blogs"
      const posts = blogsData.posts || blogsData.blogs || [];
      if (posts.length === 0) {
        // Check if category exists by trying to get all categories
        const categoriesData = await blogService.getCategories();
        const categoryExists = categoriesData.find(cat => cat.slug === categorySlug);
        
        if (!categoryExists) {
          setNotFound(true);
          return;
        }
        
        // Category exists but has no posts
        setCategory(categoryExists);
        setBlogs([]);
        setTotalPages(0);
      } else {
        // Set category from first blog post or find it in categories
        if (posts[0].categories && posts[0].categories.length > 0) {
          const categoryFromPost = posts[0].categories.find(cat => 
            (cat.slug || cat) === categorySlug
          );
          setCategory(categoryFromPost || { name: categorySlug, slug: categorySlug });
        } else {
          setCategory({ name: categorySlug, slug: categorySlug });
        }
        
        setBlogs(posts);
        setTotalPages(Math.ceil((blogsData.total || 0) / postsPerPage));
      }
      
    } catch (error) {
      console.error('Error loading category data:', error);
      if (error.response && error.response.status === 404) {
        setNotFound(true);
      } else {
        setError('Failed to load category data. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadBlogs = async () => {
    try {
      setError(null);
      
      const params = {
        page: currentPage,
        limit: postsPerPage
      };

      const blogsData = await blogService.getBlogsByCategory(categorySlug, params);
      // API returns "posts" array, not "blogs"
      setBlogs(blogsData.posts || blogsData.blogs || []);
      setTotalPages(Math.ceil((blogsData.total || 0) / postsPerPage));
      
    } catch (error) {
      console.error('Error loading blogs:', error);
      setError('Failed to load blog posts. Please try again later.');
      setBlogs([]);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 400, behavior: "smooth" });
  };

  const handleReadMore = (slug) => {
    navigate(`/blog/${slug}`);
  };

  const getCategoryDisplayName = (categoryName) => {
    if (!categoryName) return 'Category';
    return categoryName.charAt(0).toUpperCase() + categoryName.slice(1);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Error components
  const ErrorMessage = ({ message, onRetry }) => (
    <div className="ErrorMessage">
      <div className="text-center py-5">
        <i className="fas fa-exclamation-triangle fa-3x mb-3" style={{color: '#ff6b6b'}}></i>
        <h4 className="Head_4 mb-3">Oops! Something went wrong</h4>
        <p className="Pra_1 mb-4">{message}</p>
        <button className="BtnFill" onClick={onRetry}>
          <i className="fas fa-redo me-2"></i>
          Try Again
        </button>
      </div>
    </div>
  );

  const NotFoundMessage = () => (
    <div className="NotFoundMessage">
      <div className="text-center py-5">
        <i className="fas fa-folder-open fa-3x mb-3" style={{color: '#ff9900'}}></i>
        <h4 className="Head_4 mb-3">Category Not Found</h4>
        <p className="Pra_1 mb-4">The category you're looking for doesn't exist.</p>
        <button className="BtnFill" onClick={() => navigate('/blog')}>
          <i className="fas fa-arrow-left me-2"></i>
          Back to Blog
        </button>
      </div>
    </div>
  );

  if (loading) {
    return <Loader />;
  }

  if (notFound) {
    return (
      <>
        <Header />
        <section className="BlogCategorySection">
          <div className="container">
            <NotFoundMessage />
          </div>
        </section>
        <Footer />
      </>
    );
  }

  const categoryName = category?.name || categorySlug;

  return (
    <>
      <Header />
      
      {/* Breadcrumb Navigation */}
      <section className="BreadcrumbSection">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
                  <i className="fas fa-home me-2"></i>Home
                </a>
              </li>
              <li className="breadcrumb-item">
                <a href="/blog" onClick={(e) => { e.preventDefault(); navigate('/blog'); }}>
                  Blog
                </a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {getCategoryDisplayName(categoryName)}
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Category Hero Section */}
      <section className="BlogCategoryHeroSection">
        <div className="container">
          <div className="CustomHeading text-center">
            <h1 className="Head_1">{getCategoryDisplayName(categoryName)} Articles</h1>
            <h2 className="Head_2">Expert insights and advice</h2>
            {category?.description && (
              <p className="Pra_1" style={{ textAlign: 'center', margin: '0 auto', display: 'block', width: '100%', maxWidth: '600px' }}>
                {category.description}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Blog Grid Section */}
      <section className="BlogListSection">
        <div className="container">
          {/* Error Message */}
          {error && (
            <ErrorMessage 
              message={error} 
              onRetry={() => {
                setError(null);
                loadCategoryData();
              }} 
            />
          )}

          {/* Blog Grid */}
          {!error && (
            <div className="BlogGrid">
              <div className="row">
                {blogs.map((post) => (
                  <div key={post._id || post.id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4">
                    <article className="BlogCard">
                      <div className="BlogCardImage">
                        <img src={post.featuredImage || post.image} alt={post.title} loading="lazy" />
                        <div className="CategoryBadge">
                          {getCategoryDisplayName(categoryName)}
                        </div>
                      </div>
                      <div className="BlogCardContent">
                        <div className="BlogMeta">
                          <span className="Date">
                            <i className="fas fa-calendar-alt me-2"></i>
                            {formatDate(post.publishDate || post.createdAt)}
                          </span>
                          <span className="ReadTime">
                            <i className="fas fa-clock me-2"></i>
                            {post.readingTime || post.readTime || 5} min
                          </span>
                        </div>
                        <h4 className="Head_5 rich-content" dangerouslySetInnerHTML={{ __html: post.title }} />
                        <p className="Pra_1 rich-content" dangerouslySetInnerHTML={{ __html: post.excerpt || post.summary }} />
                        <button 
                          className="ReadMoreBtn"
                          onClick={() => handleReadMore(post.slug)}
                        >
                          Read More
                          <i className="fas fa-arrow-right ms-2"></i>
                        </button>
                      </div>
                    </article>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination */}
          {!error && totalPages > 1 && (
            <div className="BlogPagination">
              <nav aria-label="Blog pagination">
                <div className="pagination-wrapper">
                  <button
                    className={`pagination-btn prev ${currentPage === 1 ? 'disabled' : ''}`}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className="fas fa-chevron-left me-2"></i>
                    Previous
                  </button>
                  
                  <div className="page-numbers">
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      return (
                        <button
                          key={pageNumber}
                          className={`page-number ${currentPage === pageNumber ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    className={`pagination-btn next ${currentPage === totalPages ? 'disabled' : ''}`}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <i className="fas fa-chevron-right ms-2"></i>
                  </button>
                </div>
              </nav>
            </div>
          )}

          {/* No Results Message */}
          {!error && blogs.length === 0 && !loading && (
            <div className="NoResults">
              <div className="text-center py-5">
                <i className="fas fa-search fa-3x mb-3" style={{color: '#ff9900'}}></i>
                <h4 className="Head_4 mb-3">No articles found</h4>
                <p className="Pra_1 mb-4">There are no articles in this category yet. Check back later for new content.</p>
                <button 
                  className="BtnBorder"
                  onClick={() => navigate('/blog')}
                >
                  View All Articles
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
};

export default BlogCategory;